import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import { useRouters } from '../api/queries';
import type { AuditLogDto, Paginated } from '../api/types';
import { Badge, Card, Empty, Pagination, Spinner } from '../components/ui';
import { formatDateTime } from '../lib/format';

/** Acciones que conviene distinguir de un vistazo en una tabla larga. */
function actionTone(action: string): 'neutral' | 'ok' | 'warn' | 'bad' | 'info' {
  if (action.endsWith('_DELETED') || action === 'LOGIN_FAILED') return 'bad';
  if (action.endsWith('_CREATED')) return 'ok';
  if (action.endsWith('_UPDATED') || action.endsWith('_CHANGED')) return 'warn';
  if (action === 'LOGIN' || action === 'LOGOUT') return 'info';
  return 'neutral';
}

export function AuditPage() {
  const routers = useRouters();
  const [routerId, setRouterId] = useState('');
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const facets = useQuery({
    queryKey: ['audit', 'facets'],
    queryFn: () => api.get<{ actions: string[]; entities: string[] }>('/audit/facets'),
    staleTime: 300_000,
  });

  const logs = useQuery({
    queryKey: ['audit', { routerId, action, search, page }],
    queryFn: () =>
      api.get<Paginated<AuditLogDto>>('/audit', {
        routerId: routerId || undefined,
        action: action || undefined,
        search: search || undefined,
        page,
        pageSize: 40,
      }),
  });

  const reset = () => setPage(1);

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Auditoría</h1>
          <p>
            Registro de sólo escritura de todas las operaciones. Conserva el email del autor incluso
            si la cuenta se elimina después.
          </p>
        </div>
      </div>

      <div className="toolbar">
        <select
          value={routerId}
          onChange={(event) => {
            setRouterId(event.target.value);
            reset();
          }}
          style={{ width: 'auto' }}
        >
          <option value="">Todos los routers</option>
          {(routers.data ?? []).map((router) => (
            <option key={router.id} value={router.id}>
              {router.name}
            </option>
          ))}
        </select>

        <select
          value={action}
          onChange={(event) => {
            setAction(event.target.value);
            reset();
          }}
          style={{ width: 'auto' }}
        >
          <option value="">Todas las acciones</option>
          {(facets.data?.actions ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            reset();
          }}
          placeholder="Buscar por email o id…"
          style={{ width: 240 }}
        />

        {logs.isFetching && <Spinner label="" />}
      </div>

      <Card
        footer={
          logs.data && logs.data.meta.total > 0 ? (
            <Pagination
              page={logs.data.meta.page}
              totalPages={logs.data.meta.totalPages}
              total={logs.data.meta.total}
              onPage={setPage}
            />
          ) : undefined
        }
      >
        {logs.isLoading ? (
          <div className="card-body">
            <Spinner />
          </div>
        ) : logs.data && logs.data.data.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>Autor</th>
                  <th>Acción</th>
                  <th>Entidad</th>
                  <th>Router</th>
                  <th>IP</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {logs.data.data.map((log) => (
                  // La fila de detalle es hermana de la principal, así que el par va en un
                  // Fragment con la key: una tabla no admite un wrapper entre tbody y tr.
                  <Fragment key={log.id}>
                    <tr>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.createdAt)}</td>
                      <td className="mono">{log.actorEmail}</td>
                      <td>
                        <Badge tone={actionTone(log.action)}>{log.action}</Badge>
                      </td>
                      <td>
                        {log.entity}
                        {log.entityId && (
                          <div className="faint mono" style={{ fontSize: 11 }}>
                            {log.entityId.slice(0, 8)}
                          </div>
                        )}
                      </td>
                      <td>{log.routerName ?? <span className="faint">—</span>}</td>
                      <td className="mono faint">{log.ipAddress ?? '—'}</td>
                      <td className="actions">
                        {log.metadata ? (
                          <button
                            type="button"
                            className="small ghost"
                            onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                          >
                            {expanded === log.id ? 'Ocultar' : 'Detalle'}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                    {expanded === log.id && (
                      <tr>
                        <td colSpan={7} style={{ background: 'var(--bg-subtle)' }}>
                          <pre
                            className="mono"
                            style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}
                          >
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                          {log.userAgent && (
                            <div className="faint" style={{ fontSize: 11, marginTop: 6 }}>
                              {log.userAgent}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No hay entradas de auditoría con esos filtros.</Empty>
        )}
      </Card>
    </main>
  );
}
